import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";


// const API_BASE = "https://hopeful-cosmic-koala.ngrok-free.app/api";
const API_BASE = "/api";

const PastorMessage = () => {
  const [messages, setMessages]           = useState([]);
  const [categories, setCategories]       = useState(["All"]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [searchTerm, setSearchTerm]       = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isPlaying, setIsPlaying]         = useState(false);
  const [currentTime, setCurrentTime]     = useState(0);
  const [duration, setDuration]           = useState(0);
  const audioRef = useRef(null);

  // ── Fetch messages ────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE}/pastor-messages/`, {
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "69420",
          },
        });
        if (!response.ok) throw new Error(`Failed to fetch messages: ${response.status}`);
        const data = await response.json();
        const results = Array.isArray(data) ? data : (data.results ?? []);

        // Only show published messages
        const published = results.filter((m) => m.status === "published");
        setMessages(published);

        // Derive category list from results
        const uniqueCategories = [
          "All",
          ...Array.from(
            new Map(
              published
                .filter((m) => m.category)
                .map((m) => [m.category.slug, m.category.name])
            ).values()
          ),
        ];
        setCategories(uniqueCategories);
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, []);

  // ── Filter ────────────────────────────────────────────────────────────────
  const filteredMessages = messages.filter((message) => {
    const matchesCategory =
      activeCategory === "All" || message.category?.name === activeCategory;
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      (message.title || "").toLowerCase().includes(term) ||
      (message.excerpt || "").toLowerCase().includes(term) ||
      (message.scripture_reference || "").toLowerCase().includes(term);
    return matchesCategory && matchesSearch;
  });

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  // ── Audio player ──────────────────────────────────────────────────────────
  const openAudioModal = (message) => {
    setSelectedMessage(message);
    setShowAudioModal(true);
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const closeAudioModal = () => {
    if (audioRef.current) audioRef.current.pause();
    setShowAudioModal(false);
    setIsPlaying(false);
    setSelectedMessage(null);
  };

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => setCurrentTime(audioRef.current.currentTime);
  const handleLoadedMetadata = () => setDuration(audioRef.current.duration);

  const handleSeek = (e) => {
    const seekTime = (e.target.value / 100) * duration;
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const downloadAudio = (message) => {
    const link = document.createElement("a");
    link.href = message.audio_url;
    link.download = `${message.title}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    document.body.style.overflow = showAudioModal ? "hidden" : "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [showAudioModal]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css" />

      <div className="pastor-message-page">

        {/* HERO */}
        <section className="pastor-hero">
          <div className="hero-overlay">
            <div className="container">
              <div className="row align-items-center">
                <div className="col-lg-6 text-white mb-4 mb-lg-0">
                  <h1 className="display-3 fw-bold mb-3">Pastor's Messages</h1>
                 
                  <p className="lead mb-0">
                  
                    God's word is sufficient to destroy sin, sicknesses, aging and death out of your life and cause a permanent transformation.
                  
                  </p>
                </div>
                <div className="col-lg-6">
                  <div className="search-container">
                    <div className="input-group input-group-lg shadow-lg">
                      <span className="input-group-text bg-white border-0">
                        <i className="bi bi-search text-primary fs-5"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-0"
                        placeholder="Search messages, topics, scriptures..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PASTOR INFO BANNER */}
        <section className="py-4 bg-light border-bottom">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-8">
                <div className="d-flex align-items-center gap-3">
                  <img
                    src="/images/pastor3.png"
                    alt="Pastor"
                    className="rounded-circle shadow-sm"
                    style={{ width: "80px", height: "80px", objectFit: "cover" }}
                  />
                  <div>
                    <h5 className="fw-bold mb-1 text-primary">Pr Keffah Evans Muwanguzi</h5>
                    <p className="mb-0 text-muted">Senior Pastor, Gospel City Church International</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4 text-md-end mt-3 mt-md-0">
                <button className="btn btn-primary rounded-pill px-4">
                  <i className="bi bi-bell me-2"></i>Subscribe for Updates
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* CATEGORIES */}
        <section className="py-4 bg-white sticky-top shadow-sm" style={{ top: "0", zIndex: "1020" }}>
          <div className="container">
            <div className="d-flex flex-wrap gap-2 justify-content-center">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`btn ${activeCategory === category ? "btn-primary" : "btn-outline-primary"} rounded-pill px-4`}
                  onClick={() => setActiveCategory(category)}
                >
                  <i className="bi bi-bookmark me-2"></i>
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* MESSAGES LIST */}
        <section className="py-5 bg-light">
          <div className="container">

            {/* Loading */}
            {loading && (
              <div className="d-flex justify-content-center align-items-center py-5">
                <div style={{ width: 44, height: 44, border: "4px solid #e8f4fd", borderTopColor: "#0b1c6b", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}

            {/* Error */}
            {!loading && error && (
              <div className="text-center py-5">
                <div style={{ fontSize: "3.5rem", marginBottom: 16 }}>⚠️</div>
                <h4 style={{ color: "#0b1c6b" }}>Could not load messages</h4>
                <p className="text-muted">{error}</p>
              </div>
            )}

            {/* Content */}
            {!loading && !error && (
              <>
                <div className="row mb-4">
                  <div className="col">
                    <p className="text-muted mb-0">
                      <i className="bi bi-mic-fill me-2"></i>
                      Showing {filteredMessages.length} message{filteredMessages.length !== 1 ? "s" : ""}
                      {activeCategory !== "All" && ` in ${activeCategory}`}
                    </p>
                  </div>
                </div>

                {filteredMessages.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox fs-1 text-muted mb-3 d-block"></i>
                    <h5 className="text-muted">No messages found</h5>
                    <p className="text-muted">Try adjusting your search or category filter</p>
                  </div>
                ) : (
                  <div className="row g-4">
                    {filteredMessages.map((message) => (
                      <div className="col-12" key={message.id}>
                        <article className="card border-0 shadow-sm rounded-4 overflow-hidden message-card">
                          <div className="row g-0">
                            <div className="col-md-4 col-lg-3">
                              <div className="position-relative h-100">
                                {message.cover_image ? (
                                  <img
                                    src={message.cover_image}
                                    alt={message.title}
                                    className="img-fluid w-100 h-100"
                                    style={{ objectFit: "cover", minHeight: "280px" }}
                                  />
                                ) : (
                                  <div style={{ minHeight: "280px", background: "linear-gradient(135deg,#0b1c6b,#159EEC)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "4rem" }}>
                                    🎙️
                                  </div>
                                )}
                                {message.audio_duration && (
                                  <div className="position-absolute top-0 end-0 m-3">
                                    <span className="badge bg-primary px-3 py-2">
                                      <i className="bi bi-clock me-1"></i>
                                      {message.audio_duration}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="col-md-8 col-lg-9">
                              <div className="card-body p-4 p-lg-5">
                                <div className="mb-3">
                                  {message.category && (
                                    <span className="badge bg-info bg-opacity-10 text-info text-uppercase me-2">
                                      <i className="bi bi-folder me-1"></i>
                                      {message.category.name}
                                    </span>
                                  )}
                                </div>

                                <h2 className="card-title display-6 fw-bold text-primary mb-3">
                                  {message.title}
                                </h2>

                                <div className="d-flex flex-wrap align-items-center gap-4 mb-3">
                                  <span className="text-muted">
                                    <i className="bi bi-calendar3 me-2"></i>
                                    {formatDate(message.published_date)}
                                  </span>
                                  {message.scripture_reference && (
                                    <span className="text-primary fw-bold">
                                      <i className="bi bi-book me-2"></i>
                                      {message.scripture_reference}
                                    </span>
                                  )}
                                </div>

                                <p className="card-text fs-6 lh-lg mb-4 text-muted">
                                  {message.excerpt}
                                </p>

                                <div className="d-flex flex-wrap gap-2">
                                  <Link
                                    to={`/pastor-messages/${message.slug}`}
                                    className="btn btn-primary rounded-pill px-4"
                                  >
                                    <i className="bi bi-book-half me-2"></i>
                                    Read Full Message
                                  </Link>

                                  {/* Audio buttons — only if audio_url exists */}
                                  {message.audio_url && (
                                    <>
                                      <button
                                        className="btn btn-outline-primary rounded-pill px-4"
                                        onClick={() => openAudioModal(message)}
                                      >
                                        <i className="bi bi-play-circle me-2"></i>
                                        Listen Audio
                                      </button>
                                      <button
                                        className="btn btn-outline-secondary rounded-pill px-4"
                                        onClick={() => downloadAudio(message)}
                                      >
                                        <i className="bi bi-download me-2"></i>
                                        Download
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </article>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* AUDIO MODAL — only rendered when a message with audio is selected */}
        {showAudioModal && selectedMessage && selectedMessage.audio_url && (
          <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0 shadow-lg rounded-4">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold text-primary">
                    <i className="bi bi-headphones me-2"></i>Now Playing
                  </h5>
                  <button type="button" className="btn-close" onClick={closeAudioModal}></button>
                </div>
                <div className="modal-body p-4">
                  <div className="row align-items-center mb-4">
                    <div className="col-auto">
                      {selectedMessage.cover_image ? (
                        <img
                          src={selectedMessage.cover_image}
                          alt={selectedMessage.title}
                          className="rounded-3 shadow-sm"
                          style={{ width: "100px", height: "100px", objectFit: "cover" }}
                        />
                      ) : (
                        <div style={{ width: 100, height: 100, borderRadius: 12, background: "linear-gradient(135deg,#0b1c6b,#159EEC)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2.5rem" }}>
                          🎙️
                        </div>
                      )}
                    </div>
                    <div className="col">
                      {selectedMessage.category && (
                        <span className="badge bg-info bg-opacity-10 text-info text-uppercase mb-2">
                          {selectedMessage.category.name}
                        </span>
                      )}
                      <h5 className="fw-bold mb-1">{selectedMessage.title}</h5>
                      <p className="text-muted mb-0">
                        {selectedMessage.scripture_reference && (
                          <><i className="bi bi-book me-1"></i>{selectedMessage.scripture_reference} • </>
                        )}
                        {formatDate(selectedMessage.published_date)}
                      </p>
                    </div>
                  </div>

                  <div className="audio-player-modal bg-light rounded-4 p-4">
                    <div className="text-center mb-4">
                      <button
                        onClick={togglePlay}
                        className="btn btn-primary rounded-circle play-btn-large shadow"
                      >
                        <i className={`bi ${isPlaying ? "bi-pause-fill" : "bi-play-fill"} fs-1`}></i>
                      </button>
                    </div>

                    <div className="mb-3">
                      <input
                        type="range"
                        className="form-range"
                        min="0"
                        max="100"
                        value={duration ? (currentTime / duration) * 100 : 0}
                        onChange={handleSeek}
                      />
                    </div>

                    <div className="d-flex justify-content-between text-muted mb-3">
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </div>

                    <div className="d-flex justify-content-center gap-3">
                      <button
                        className="btn btn-outline-primary btn-sm rounded-pill"
                        onClick={() => {
                          if (audioRef.current)
                            audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
                        }}
                      >
                        <i className="bi bi-skip-backward-fill me-1"></i>10s
                      </button>
                      <button
                        className="btn btn-outline-primary btn-sm rounded-pill"
                        onClick={() => downloadAudio(selectedMessage)}
                      >
                        <i className="bi bi-download me-1"></i>Download
                      </button>
                      <button
                        className="btn btn-outline-primary btn-sm rounded-pill"
                        onClick={() => {
                          if (audioRef.current)
                            audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 10);
                        }}
                      >
                        10s<i className="bi bi-skip-forward-fill ms-1"></i>
                      </button>
                    </div>

                    <audio
                      ref={audioRef}
                      src={selectedMessage.audio_url}
                      onTimeUpdate={handleTimeUpdate}
                      onLoadedMetadata={handleLoadedMetadata}
                      onEnded={() => setIsPlaying(false)}
                    />
                  </div>
                </div>
                <div className="modal-footer border-0 pt-0">
                  <Link
                    to={`/pastor-messages/${selectedMessage.slug}`}
                    className="btn btn-outline-primary rounded-pill px-4"
                  >
                    <i className="bi bi-book-half me-2"></i>Read Full Message
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SOCIAL */}
        <section className="social-section py-5 text-center text-white">
          <div className="container py-4">
            <h2 className="display-5 fw-bold mb-3">Follow Us</h2>
            <p className="fs-6 mb-4 mx-auto" style={{ maxWidth: "600px" }}>
              Stay connected with Gospel City Church International through our social platforms.
            </p>
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <a href="https://www.linkedin.com/in/gospel-city-church-international-650a562a0/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-linkedin fs-4"></i>
              </a>
              <a href="https://groups.imo.im/.5l9akqTOrYa5Tv4T4" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
            <i className="bi bi-chat-dots fs-4"></i>
            </a>
              <a href="https://x.com/GospelCityChur?t=x8dsFbFkghmYh02lV0FVxQ&s=03" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-twitter-x fs-4"></i>
              </a>
              <a href="https://whatsapp.com/channel/0029Vb769UyDzgTBAZZcb40F" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-whatsapp fs-4"></i>
              </a>
              <a href="https://youtube.com/@gospelcitychurchinternational?si=1pZx2ak4ss3b4HuU" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-youtube fs-4"></i>
              </a>
            </div>
          </div>
        </section>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700;800&display=swap');
          * { font-family: 'Lexend', sans-serif; }

          .pastor-hero {
            position: relative;
            background-image: url('/images/pastor1.png');
            background-size: cover;
            background-position: center;
            min-height: 500px;
            display: flex;
            align-items: center;
          }
          .hero-overlay { position: relative; width: 100%; z-index: 2; padding: 80px 0; }
          .pastor-hero::before {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(135deg, rgba(11,28,107,0.95), rgba(21,158,236,0.85));
            z-index: 1;
          }
          .search-container .form-control { padding: 14px 20px; font-size: 16px; }
          .search-container .form-control:focus { box-shadow: 0 0 0 0.25rem rgba(26,58,161,0.25); }
          .search-container .input-group-text { padding: 14px 20px; }
          .message-card { transition: transform 0.3s ease, box-shadow 0.3s ease; }
          .message-card:hover { transform: translateY(-5px); box-shadow: 0 20px 50px rgba(26,58,161,0.2) !important; }
          .modal { animation: fadeIn 0.3s ease; }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          .play-btn-large {
            width: 90px; height: 90px;
            display: flex; align-items: center; justify-content: center;
            transition: all 0.3s;
          }
          .play-btn-large:hover { transform: scale(1.05); box-shadow: 0 10px 30px rgba(11,28,107,0.3); }
          .form-range::-webkit-slider-thumb { background: #0b1c6b; }
          .form-range::-moz-range-thumb { background: #0b1c6b; }
          .form-range::-webkit-slider-runnable-track { background: #e9ecef; height: 8px; border-radius: 4px; }
          .text-primary { color: #1a3aa1 !important; }
          .text-info { color: #159EEC !important; }
          .bg-info { background-color: #159EEC !important; }
          .bg-primary { background-color: #0b1c6b !important; }
          .btn-primary { background: #0b1c6b; border-color: #0b1c6b; }
          .btn-primary:hover { background: #159EEC; border-color: #159EEC; }
          .btn-outline-primary { color: #0b1c6b; border-color: #0b1c6b; }
          .btn-outline-primary:hover { background: #0b1c6b; border-color: #0b1c6b; color: #fff; }
          .social-section { background: linear-gradient(135deg,#0b1c6b,#159EEC); }
          .social-icon { width: 52px; height: 52px; display: flex; align-items: center; justify-content: center; transition: all 0.3s; }
          .social-icon:hover { background: rgba(255,255,255,0.3); transform: translateY(-4px) scale(1.1); }
          @media (max-width: 768px) {
            .pastor-hero { min-height: 400px; }
            .display-3 { font-size: 2.5rem; }
            .message-card .row { flex-direction: column; }
            .play-btn-large { width: 70px; height: 70px; }
          }
        `}</style>
      </div>
    </>
  );
};

export default PastorMessage;